import React, { useEffect, useState } from "react";
import { Alert, Col, Form, Input, InputNumber, Row, Select, Space, Switch, Tooltip, Typography } from "antd";
import { ApiOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { FTP_ENDPOINT, FTP_PROTOCOL, FTP_PROTOCOL_OPTIONS } from "../constants";

const { Text } = Typography;

const EMPTY = {
  protocol: FTP_PROTOCOL.FTP,
  port: 21,
  usePassive: true,
  validateCertificate: false,
  isActive: true,
};

/**
 * Create/edit form for one FTP target.
 *
 * The stored password is never sent to the browser, so on an edit the field starts empty and
 * an empty field means "keep the one on file" — both here and on the server.
 */
const FtpConnectionModal = ({ open, connection, onClose, onSaved }) => {
  const { mutate } = useMutations();
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const editing = Boolean(connection?.ftpConnectionId);

  useEffect(() => {
    if (!open) return;

    setTestResult(null);
    form.setFieldsValue(
      connection
        ? {
            name: connection.name,
            protocol: connection.protocol,
            host: connection.host,
            port: connection.port,
            username: connection.username,
            password: "",
            remoteDirectory: connection.remoteDirectory,
            usePassive: connection.usePassive,
            validateCertificate: connection.validateCertificate,
            isActive: connection.isActive,
          }
        : { ...EMPTY, name: "", host: "", username: "", password: "", remoteDirectory: "" }
    );
  }, [open, connection, form]);

  /** Everything the API needs, straight off the form. */
  const payload = (values) => ({
    name: (values.name || "").trim(),
    protocol: values.protocol ?? FTP_PROTOCOL.FTP,
    host: (values.host || "").trim(),
    port: values.port ?? 21,
    username: (values.username || "").trim(),
    password: values.password || "",
    remoteDirectory: (values.remoteDirectory || "").trim() || null,
    usePassive: values.usePassive ?? true,
    validateCertificate: values.validateCertificate ?? false,
    isActive: values.isActive ?? true,
  });

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);

    await mutate("saveFtpConnection", {
      method: editing ? "put" : "post",
      endpoint: editing ? `${FTP_ENDPOINT}/${connection.ftpConnectionId}` : FTP_ENDPOINT,
      payload: payload(values),
      onSuccess: () => {
        onSaved?.();
        onClose?.();
      },
    });

    setSaving(false);
  };

  const test = async () => {
    const values = await form.validateFields(["host", "username", "name"]);
    setTesting(true);
    setTestResult(null);

    await mutate("testFtpConnection", {
      method: "post",
      endpoint: `${FTP_ENDPOINT}/test`,
      payload: {
        ...payload({ ...form.getFieldsValue(), ...values }),
        // Lets the server fall back to the stored password when the field was left blank.
        ftpConnectionId: connection?.ftpConnectionId ?? null,
      },
      onSuccess: setTestResult,
    });

    setTesting(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${connection.name}` : "New FTP connection"}
      size="lg"
      confirmText="Save"
      loading={saving}
      onConfirm={save}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Name"
              tooltip="Shown wherever a target has to be picked."
              rules={[{ required: true, message: "A name is required." }]}
            >
              <Input placeholder="e.g. Off-site NAS" maxLength={100} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item name="protocol" label="Protocol">
              <Select
                options={FTP_PROTOCOL_OPTIONS.map(({ value, label }) => ({ value, label }))}
                onChange={(value) => {
                  const meta = FTP_PROTOCOL_OPTIONS.find((p) => p.value === value);
                  form.setFieldValue("port", meta?.defaultPort ?? 21);
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={16} md={16}>
            <Form.Item
              name="host"
              label="Host"
              rules={[{ required: true, message: "The host is required." }]}
            >
              <Input placeholder="ftp.example.com" autoComplete="off" />
            </Form.Item>
          </Col>

          <Col xs={8} md={8}>
            <Form.Item name="port" label="Port">
              <InputNumber min={1} max={65535} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: "The username is required." }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="password"
              label="Password"
              tooltip={
                editing
                  ? "Leave empty to keep the stored password."
                  : "Stored encrypted; it is never sent back to this screen."
              }
              rules={
                editing
                  ? []
                  : [{ required: true, message: "A password is required." }]
              }
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={editing ? "unchanged" : ""}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="remoteDirectory"
              label="Remote directory"
              tooltip="Where archives are dropped. Empty means the login directory. Created if it does not exist."
            >
              <Input placeholder="/backups/kubitai" maxLength={400} />
            </Form.Item>
          </Col>

          <Col xs={8} md={4}>
            <Form.Item
              name="usePassive"
              label="Passive"
              valuePropName="checked"
              tooltip="Leave on unless the server requires active mode."
            >
              <Switch />
            </Form.Item>
          </Col>

          <Col xs={8} md={4}>
            <Form.Item
              name="validateCertificate"
              label={
                <Space size={6}>
                  Verify TLS
                  <Tooltip title="Only for the FTPS protocols. Off accepts a self-signed certificate, which most internal servers present.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>

          <Col xs={8} md={4}>
            <Form.Item
              name="isActive"
              label="Active"
              valuePropName="checked"
              tooltip="An inactive target stays configured but is not offered for uploads."
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Button variant="secondary" icon={<ApiOutlined />} loading={testing} onClick={test}>
            Test connection
          </Button>

          {testResult && (
            <Alert
              type={testResult.connected && testResult.canWrite ? "success" : "error"}
              showIcon
              message={
                testResult.connected
                  ? testResult.canWrite
                    ? `Connected and wrote a test file in ${testResult.elapsedMs} ms`
                    : "Connected, but the directory is not writable"
                  : "Could not connect"
              }
              description={
                testResult.connected ? (
                  <Space direction="vertical" size={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Uploads land in <Text code>{testResult.remoteDirectory || "/"}</Text>
                      {testResult.remoteDirectoryExists ? "" : " (created on first upload)"}
                    </Text>
                    {testResult.error && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {testResult.error}
                      </Text>
                    )}
                  </Space>
                ) : (
                  testResult.error
                )
              }
            />
          )}
        </Space>
      </Form>
    </Modal>
  );
};

export default FtpConnectionModal;
